import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronUp,
  faTriangleExclamation,
} from '@fortawesome/sharp-solid-svg-icons';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import Ticker from '@/components/common/ticker/ticker';
import Divider from '@/components/divider';
import Typography from '@/components/typography';
import { GLOBAL_TOOLTIP } from '@/constants/constants';
import {
  type CombinedFeatureTypes,
  FeatureGroupsEnum,
  featureGroupTitles,
  FeatureTypeNonFeatureSliceEnum,
  FeatureTypeSelectionsPanelEnum,
  featureTypeTitles,
} from '@/features/selectionsPanel/constants';
import { AcresFeature } from '@/models/features';
import { isBoundaryFeature } from '@/models/features/guards';
import { useAuth } from '@/providers/auth-provider';
import { useFileManagementContext } from '@/providers/FileManagementProvider';

import CustomizeLandHideAllButton from './customAccordianContent/customizeLandHideAllButton';
import SelectionsPanelOverflowMenu from './overflowMenu';

type Props = {
  selectionType: CombinedFeatureTypes | FeatureGroupsEnum;
  selections: AcresFeature[];
  darkAccordion: boolean;
  content: ReactNode;
  contentClassName?: string;
};

const SelectionsAccordion: FC<Props> = ({
  selectionType,
  selections,
  darkAccordion = true,
  content,
  contentClassName = undefined,
}) => {
  const accordionRef = useRef<HTMLDivElement>(null);
  const { activeFileId } = useFileManagementContext();
  const [isExpanded, setIsExpanded] = useState(true);
  const { isFreeOrAbove, userCanAssociateComps } = useAuth();

  const warningMap = useMemo(
    () => ({
      [FeatureTypeSelectionsPanelEnum.REAL_ESTATE]:
        'Listing selections cannot be saved to your map.',
      [FeatureTypeSelectionsPanelEnum.COMP]:
        'Sold Land selections will not be saved. Upgrade to Enterprise to unlock this feature.',
    }),
    []
  );

  const title =
    featureTypeTitles[selectionType] ?? featureGroupTitles[selectionType];

  const totalSelections = useMemo(() => selections.length, [selections.length]);

  const isSupportedType = useMemo(
    () =>
      selectionType === FeatureTypeSelectionsPanelEnum.PARCEL ||
      selectionType === FeatureTypeSelectionsPanelEnum.FSA ||
      selectionType === FeatureTypeSelectionsPanelEnum.UPLOAD ||
      selectionType === FeatureTypeSelectionsPanelEnum.BOUNDARY ||
      selectionType === FeatureGroupsEnum.BOUNDARIES,
    [selectionType]
  );

  const isOnlyOneBoundary = useMemo(() => {
    return (
      isSupportedType &&
      totalSelections === 1 &&
      isBoundaryFeature(selections[0])
    );
  }, [isSupportedType, selections, totalSelections]);

  useEffect(() => {
    if (
      activeFileId &&
      accordionRef.current &&
      selectionType === FeatureTypeNonFeatureSliceEnum.CUSTOMIZATIONS
    ) {
      accordionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [activeFileId, selectionType]);

  const handleOnClickAccordionHeader = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const showOverflowMenu = useMemo(() => {
    return isSupportedType && isFreeOrAbove && !isOnlyOneBoundary;
  }, [isFreeOrAbove, isOnlyOneBoundary, isSupportedType]);

  const saveWarning = useMemo(() => {
    if (
      selectionType === FeatureTypeSelectionsPanelEnum.REAL_ESTATE ||
      (selectionType === FeatureTypeSelectionsPanelEnum.COMP &&
        !userCanAssociateComps)
    ) {
      return warningMap[selectionType];
    }
    return null;
  }, [selectionType, userCanAssociateComps, warningMap]);

  const extraButtonMap = {
    [FeatureTypeNonFeatureSliceEnum.CUSTOMIZATIONS]: (
      <CustomizeLandHideAllButton
        className={twMerge(
          'flex h-10 w-10 items-center justify-center p-0',
          darkAccordion ? 'hover:bg-gray-80' : 'text-gray-100 hover:bg-gray-40'
        )}
        iconClassName="h-4 w-4 p-0"
      />
    ),
  };

  return (
    <div ref={accordionRef} className="w-full border-b-[1px] border-b-gray-20">
      <span
        className={twMerge(
          'flex h-[40px] w-full items-center overflow-hidden pl-3 text-white',
          saveWarning && 'bg-gray-60',
          darkAccordion ? 'bg-gray-70' : 'border-b bg-gray-30 '
        )}
        role="button"
        tabIndex={0}
        onClick={handleOnClickAccordionHeader}
        onKeyDown={(e) =>
          (e.code === 'Space' || e.code === 'Enter') &&
          handleOnClickAccordionHeader()
        }
      >
        {saveWarning && (
          <div
            className="mr-2 h-5 leading-5"
            data-tooltip-content={saveWarning}
            data-tooltip-delay-show={300}
            data-tooltip-id={GLOBAL_TOOLTIP}
          >
            <FontAwesomeIcon
              className="text-yellow-utility"
              icon={faTriangleExclamation}
            />
          </div>
        )}

        <Typography
          className={twMerge(
            'whitespace-nowrap',
            !darkAccordion && 'text-gray-100'
          )}
          variant="h5"
        >
          {title}
        </Typography>
        <div className="mx-2">
          <Divider
            isVertical
            className="mx-1 my-auto h-[14px] w-[1px] bg-gray-50"
          />
        </div>
        <Typography
          className={twMerge(
            'mr-auto flex flex-row flex-nowrap text-left',
            !darkAccordion && 'text-gray-100'
          )}
          variant="body2"
        >
          <Ticker value={totalSelections} />
        </Typography>
        {extraButtonMap[selectionType] || null}
        {showOverflowMenu && (
          <SelectionsPanelOverflowMenu
            buttonClassName={twMerge(
              darkAccordion
                ? 'hover:bg-gray-80'
                : 'text-gray-100 hover:bg-gray-40',
              'h-10 w-10 flex items-center justify-center'
            )}
            selectionType={selectionType}
            selections={selections}
          />
        )}
        <div
          className={twMerge(
            darkAccordion
              ? 'hover:bg-gray-80'
              : 'text-gray-100 hover:bg-gray-40',
            'flex h-10 w-10 items-center justify-center'
          )}
        >
          <FontAwesomeIcon
            className={twMerge(
              isExpanded ? ' rotate-0' : 'rotate-180',
              'h-4 w-4 transition-transform duration-300'
            )}
            icon={faChevronUp}
          />
        </div>
      </span>
      {isExpanded && (
        <div
          className={twMerge('flex-col bg-white px-2 py-2', contentClassName)}
        >
          {content}
        </div>
      )}
    </div>
  );
};

export { SelectionsAccordion };
